import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowUpRight } from "lucide-react"
import { usePortfolio } from "@/hooks/usePortfolio"
import { useStats } from "@/hooks/useStats"
import { ErrorState } from "@/components/state"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/portfolio")({
  component: Portfolio,
  head: () =>
    pageMeta({
      title: "Portfolio — Nexovia IT",
      description: "Selected work delivered by Nexovia IT.",
      path: "/portfolio",
    }),
})

function Portfolio() {
  const { data: items, isLoading, isError } = usePortfolio()
  const { data: stats, isLoading: statsLoading, isError: statsError } = useStats()
  const activeItems = (items ?? []).filter((i) => i.is_active)
  const activeStats = (stats ?? []).filter((s) => s.is_active)

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Portfolio
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl text-5xl md:text-7xl font-bold text-balance">
            Software We've Shipped <span className="text-rust">to the World.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            A selection of products, platforms and pipelines we've designed, built and scaled for clients across
            fintech, healthcare, logistics and retail.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Start a project <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              Explore our services
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      {!statsLoading && !statsError && activeStats.length > 0 && (
        <section className="border-b border-border bg-ink text-cream">
          <div
            className="mx-auto grid max-w-7xl gap-px bg-cream/10"
            style={{ gridTemplateColumns: `repeat(${Math.min(activeStats.length, 4)}, minmax(0, 1fr))` }}
          >
            {activeStats.map((s) => (
              <div key={s.id} className="bg-ink px-6 py-12 text-center">
                <div className="font-display text-4xl md:text-5xl text-rust-glow">{s.value}</div>
                <div className="mt-2 text-sm text-cream/70">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PROJECTS */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Selected work</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">Featured Projects</h2>
        </div>

        <div className="mt-14">
          {isLoading ? (
            <div className="grid gap-8 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="aspect-[16/10] bg-secondary" />
                  <div className="space-y-3 p-8">
                    <div className="h-6 w-2/3 rounded bg-secondary" />
                    <div className="h-4 w-full rounded bg-secondary" />
                    <div className="h-4 w-5/6 rounded bg-secondary" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <ErrorState message="Couldn't load the portfolio right now." />
          ) : activeItems.length === 0 ? (
            <p className="text-center text-muted-foreground">No portfolio items published yet.</p>
          ) : (
            <div className="grid gap-8 md:grid-cols-2">
              {activeItems.map((p, i) => (
                <article
                  key={p.id}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-gradient-hero">
                    {p.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p.title}
                        width={1600}
                        height={1000}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 grid-noise opacity-40" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="font-display text-5xl md:text-6xl text-cream/90">
                            {p.title.split(" ")[0]}
                          </span>
                        </div>
                      </>
                    )}
                    <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-ink/80 font-display text-xs text-cream backdrop-blur">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {p.url && (
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-cream text-ink opacity-0 shadow-elegant transition group-hover:opacity-100"
                        aria-label={`Visit ${p.title}`}
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <h3 className="text-2xl font-bold">{p.title}</h3>
                    <p className="mt-3 flex-1 text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                    {p.url && (
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-rust hover:text-rust-deep"
                      >
                        Visit project <ArrowUpRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 pb-24 md:pb-28">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 text-center shadow-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Your project next</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">Have Something to Build?</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Tell us about your product and we'll show you how we'd approach it — no obligation, just a clear plan.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Book a free consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/about" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              Meet the team
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
